import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { apiFetch } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { Play, Heart, Share2 } from 'lucide-react';

interface GifItem {
  id: string;
  type: string;
  url: string;
  title?: string;
  key?: string;
}

const getYouTubeEmbedUrl = (url: string, autoplay = false) => {
  const match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  const videoId = match?.[1];
  if (!videoId) return url;
  
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    mute: '1',
    loop: '1',
    playlist: videoId,
    controls: '0',
    modestbranding: '1',
    rel: '0',
    iv_load_policy: '3',
    fs: '0',
    disablekb: '1',
    playsinline: '1',
    widget_referrer: window.location.origin
  });
  
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

const getInstagramEmbedUrl = (url: string) => {
  if (!url) return url;
  let cleanUrl = url.split('?')[0];
  if (!cleanUrl.startsWith('http')) {
    cleanUrl = 'https://www.instagram.com/' + cleanUrl.replace(/^(www\.)?instagram\.com\//, '');
  }
  return `${cleanUrl.endsWith('/') ? cleanUrl : cleanUrl + '/'}embed/`;
};

import { toast } from 'sonner';

const ShowcaseItem = ({ item, idx }: { item: GifItem; idx: number }) => {
  const [liked, setLiked] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(item.url);
    toast.success("Link copied to clipboard!");
  };

  const toggleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLiked(!liked);
    if (!liked) {
      toast.success("Added to favorites!");
    }
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: idx * 0.1 }}
      className="w-[280px] md:w-[320px] aspect-[9/16] rounded-[2rem] overflow-hidden bg-black shadow-2xl shadow-black/5 border border-zinc-950/10 group relative"
    >
      <div className="w-full h-full relative overflow-hidden flex items-center justify-center">
        {item.type === 'YOUTUBE' ? (
          isInView ? (
            <iframe
              src={getYouTubeEmbedUrl(item.url, true)}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
             <div className="w-full h-full bg-zinc-950 flex items-center justify-center">
                <Play className="w-12 h-12 text-white/20" />
             </div>
          )
        ) : item.type === 'INSTAGRAM' ? (
          <div className="absolute inset-0 bg-white overflow-hidden">
            <iframe
              src={getInstagramEmbedUrl(item.url)}
              className="absolute inset-0 w-full h-full border-0 m-0 p-0 block"
              allowTransparency
              frameBorder="0"
              scrolling="no"
            />
          </div>
        ) : (
          item.url.toLowerCase().endsWith('.mp4') || item.url.toLowerCase().endsWith('.webm') || item.key?.toLowerCase().endsWith('.mp4') ? (
            <video 
              src={item.url} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none"
              autoPlay={isInView}
              muted 
              loop 
              playsInline
            />
          ) : (
            <img 
              src={item.url} 
              alt={item.title || `Showcase ${idx}`} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none" 
              loading="lazy"
            />
          )
        )}
      </div>
      
      <div className="absolute top-6 right-6 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0 transition-all z-20">
        <button 
          className={cn(
            "w-10 h-10 rounded-xl backdrop-blur-md border border-white/20 flex items-center justify-center transition-all",
            liked ? "bg-red-500 text-white border-red-400" : "bg-white/20 text-white hover:bg-white/40"
          )}
          onClick={toggleLike}
        >
          <Heart className={cn("w-4 h-4", liked && "fill-current")} />
        </button>
        <button 
          className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-cyan-600 transition-colors"
          onClick={handleShare}
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export const GifShowcase: React.FC = () => {
  const [items, setItems] = useState<GifItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await apiFetch('/api/showcase-gifs');
        if (response.ok) {
          const data = await response.json();
          if (data && data.gifs) {
            setItems(data.gifs);
          }
        }
      } catch (err) {
        console.error('Failed to fetch showcase items:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  if (loading) {
    return (
      <div className="w-full mt-12 overflow-x-auto no-scrollbar pb-6 px-4">
        <div className="flex gap-6 min-w-max">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="w-[280px] md:w-[320px] aspect-[9/16] rounded-[2rem] bg-zinc-100" />
          ))}
        </div>
      </div>
    );
  }

  // Hide section if no items
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="w-full mt-12 overflow-x-auto no-scrollbar pb-6 px-4">
      <div className="flex gap-6 min-w-max">
        {items.map((item, idx) => (
          <ShowcaseItem key={item.id} item={item} idx={idx} />
        ))}
      </div>
    </div>
  );
};
